import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form } from "@/components/ui/form";
import { WakeStatusStep } from "./steps/WakeStatusStep";
import { TimeStep } from "./steps/TimeStep";
import { RestfulnessStep } from "./steps/RestfulnessStep";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { SleepFormProvider } from "./SleepFormContext";

const miniFormSchema = z.object({
  has_woken_up: z.enum(['yes', 'no', 'unknown']),
  end_time: z.date().optional(),
  end_time_input: z.string().optional(),
  restfulness_id: z.string().optional(),
});

type MiniFormData = z.infer<typeof miniFormSchema>;

interface MiniSleepUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: any;
  childName?: string;
  restfulnessRatings: {id: string, description: string}[];
}

type StepType = 'wake_status' | 'end_time' | 'restfulness';

export function MiniSleepUpdateDialog({
  open,
  onOpenChange,
  session,
  childName,
  restfulnessRatings
}: MiniSleepUpdateDialogProps) {
  const [currentStep, setCurrentStep] = useState<StepType>('wake_status');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<MiniFormData>({
    resolver: zodResolver(miniFormSchema),
    defaultValues: {
      has_woken_up: session?.has_woken_up || 'unknown',
      end_time: session?.end_time ? new Date(session.end_time) : undefined,
      end_time_input: session?.end_time ? format(new Date(session.end_time), 'HH:mm') : '',
      restfulness_id: session?.restfulness_id || '',
    },
  });

  const watchedValues = form.watch();

  const determineNextStep = (): StepType | null => {
    if (currentStep === 'wake_status') {
      if (watchedValues.has_woken_up === 'yes') {
        // Check if we need restfulness
        if (!session?.restfulness_id) {
          return 'restfulness';
        }
        // Check if we need end time
        if (!session?.end_time) {
          return 'end_time';
        }
        return null; // All done
      }
      return null; // No or unknown - we're done
    }
    
    if (currentStep === 'restfulness') {
      if (!session?.end_time) {
        return 'end_time';
      }
      return null;
    }
    
    return null;
  };

  const handleNext = () => {
    const nextStep = determineNextStep();
    if (nextStep) {
      setCurrentStep(nextStep);
    } else {
      handleSave();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = form.getValues();
      
      // Determine if session should be complete
      const shouldBeComplete = 
        values.has_woken_up === 'yes' && 
        (values.restfulness_id || session?.restfulness_id) &&
        (values.end_time || session?.end_time);

      const updateData: any = {
        has_woken_up: values.has_woken_up,
        status: shouldBeComplete ? 'complete' : 'in_progress',
      };

      if (values.end_time) {
        updateData.end_time = values.end_time.toISOString();
      }

      if (values.restfulness_id) {
        updateData.restfulness_id = values.restfulness_id;
      }

      const { error } = await supabase
        .from('sleep_sessions')
        .update(updateData)
        .eq('id', session.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['incompleteSessions'] });
      queryClient.invalidateQueries({ queryKey: ['sleepSessions'] });
      queryClient.invalidateQueries({ queryKey: ['childSleepRecords'] });

      toast({
        title: "Success",
        description: shouldBeComplete 
          ? "Sleep session completed!" 
          : "Sleep session updated",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error updating session:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update sleep session",
      });
    } finally {
      setSaving(false);
    }
  };

  const getStepTitle = () => {
    const name = childName || 'baby';
    switch (currentStep) {
      case 'wake_status':
        return `Has ${name} woken up?`;
      case 'restfulness':
        return `How was ${name}'s sleep?`;
      case 'end_time':
        return `What time did ${name} wake up?`;
      default:
        return "";
    }
  };

  const canProceed = () => {
    if (currentStep === 'wake_status') {
      return !!watchedValues.has_woken_up;
    }
    if (currentStep === 'restfulness') {
      return true; // Optional
    }
    if (currentStep === 'end_time') {
      return true; // Optional
    }
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{getStepTitle()}</DialogTitle>
        </DialogHeader>
        
        <SleepFormProvider value={form as any}>
          <Form {...form}>
            <div className="space-y-4">
              {currentStep === 'wake_status' && (
                <WakeStatusStep 
                  childName={childName}
                  onNext={() => {}}
                />
              )}
              
              {currentStep === 'restfulness' && (
                <RestfulnessStep
                  restfulnessRatings={restfulnessRatings}
                  onNext={() => {}}
                />
              )}
              
              {currentStep === 'end_time' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    What time did baby wake up? (optional)
                  </p>
                  <TimeStep
                    type="end"
                    childName={childName}
                  />
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!canProceed() || saving}
                  className="flex-1"
                >
                  {saving ? "Saving..." : determineNextStep() ? "Next" : "Save"}
                </Button>
              </div>
            </div>
          </Form>
        </SleepFormProvider>
      </DialogContent>
    </Dialog>
  );
}
